/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';
import * as moment from 'moment';
import * as d3 from 'd3';
import * as Highcharts from 'highcharts';
import { RoutingService } from 'src/app/service/routing.service';
import { INTERNAL_COCKPIT_SETTINGS } from 'src/app/shared/utils/cockpit-constants';
import { QueryParams } from 'src/app/shared/models/query-params.interface';
import {
  ChartExtremes,
  NavigatorChartComponent,
} from 'src/app/shared/charts/components/navigator-chart.component';
import {
  PortfolioChartDataService,
  PortfolioChartModel,
} from './portfolio-chart-data.service';

// Query params that force a data refresh (same set as the legacy component).
const REFRESH_PARAMS = [
  'update',
  'theme',
  'id',
  'time',
  'period',
  'lang',
  'feed',
  'categories',
  'continents',
  'countries',
  'factuality',
  'temporality',
  'ranks',
  'panels',
];

const MAX_VISIBLE_SERIES = 3; // Portfolio line + first 2 entities visible on load.

/**
 * Portfolio benchmark chart (route `portfolio/benchmark`). Multi-line score chart
 * — the aggregate "Portfolio" line plus one line per portfolio entity — rendered
 * via the shared `NavigatorChartComponent`. This component only orchestrates:
 * fetch (v2 `score_ts` via the data service), build series, and wire the chart's
 * zoom → URL and legend → payload behaviors.
 */
@Component({
  selector: 'app-portfolio-chart',
  templateUrl: './portfolio-chart.component.html',
  styleUrls: ['./portfolio-chart.component.scss'],
})
export class PortfolioChartComponent implements OnInit, OnDestroy {
  loading = true;
  chartData: Highcharts.SeriesOptionsType[] = [];
  navigatorData: (number | null)[][] = [];
  valueMode: 'score' | 'percent' = 'score';
  period = 7;
  readonly maxRangeMs = INTERNAL_COCKPIT_SETTINGS.maxTimeframe * 24 * 3600000;

  private readonly colorPalette = [
    '#00fec2',
    '#18ebff',
    '#b4bdff',
    '#a351fe',
    '#fb00e6',
  ];

  private previousValue: QueryParams | undefined;
  private readonly fetch$ = new Subject<QueryParams>();
  private readonly destroy$ = new Subject<void>();

  @ViewChild(NavigatorChartComponent)
  private navigatorChart?: NavigatorChartComponent;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public routing: RoutingService,
    private dataService: PortfolioChartDataService,
  ) {}

  get theme(): 'light' | 'dark' {
    return this.routing.theme === 'light' ? 'light' : 'dark';
  }

  ngOnInit(): void {
    // Fetch is driven off a Subject so switchMap cancels superseded requests.
    // The refresh gate lives OUTSIDE this stream (only genuine changes call
    // fetch$.next), so a duplicate param emission never cancels an in-flight
    // fetch — avoiding the "stuck loading" trap.
    this.fetch$
      .pipe(
        takeUntil(this.destroy$),
        tap(() => (this.loading = true)),
        switchMap((params) => this.dataService.fetchPortfolioChartData(params)),
      )
      .subscribe((model) => {
        this.buildSeries(model);
        this.loading = false;
      });

    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params: QueryParams) => {
        this.period = params.period ? parseInt(params.period, 10) : 7;

        if (this.routing.reFresh(params, this.previousValue, ['reflow'])) {
          setTimeout(() => this.navigatorChart?.reflow(), 500);
        }
        if (this.routing.reFresh(params, this.previousValue, REFRESH_PARAMS)) {
          this.fetch$.next(params);
        }

        this.previousValue = params;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Zoom / navigator drag → push the visible range into the URL `time` param. */
  onExtremesChange(event: ChartExtremes): void {
    if (event.trigger === 'zoom' || event.trigger === 'navigator') {
      const time =
        moment(event.min).format('YYYY-MM-DD') +
        '--' +
        moment(event.max).format('YYYY-MM-DD');
      this.router.navigate([], {
        queryParams: { time, newsday: null, period: this.period },
        queryParamsHandling: 'merge',
      });
    }
  }

  private buildSeries(model: PortfolioChartModel): void {
    this.valueMode = model.isSentiment ? 'percent' : 'score';
    this.navigatorData = model.navigatorSeries.map((point) => [
      this.toTime(point.date),
      this.round2(point.score),
    ]);

    const series: Highcharts.SeriesOptionsType[] = [
      this.toSeries(model.portfolioName, null, model.portfolioSeries, 0),
    ];
    model.entities.forEach((entity, i) => {
      series.push(
        this.toSeries(entity.name, entity.compoundKey, entity.series, i + 1),
      );
    });
    this.chartData = series;
  }

  private toSeries(
    name: string,
    compoundKey: string | null,
    points: { date: string; score: number | null }[],
    index: number,
  ): Highcharts.SeriesOptionsType {
    return {
      type: 'spline',
      name,
      custom: { compound_key: compoundKey },
      lineWidth: 2,
      yAxis: 0,
      data: points.map((point) => [
        this.toTime(point.date),
        this.round2(point.score),
      ]),
      visible: index < MAX_VISIBLE_SERIES,
      showInLegend: true,
      color: this.getPaletteColor(index),
    } as Highcharts.SeriesSplineOptions;
  }

  private toTime(date: string): number {
    return moment.utc(moment(date).format('YYYY-MM-DD')).valueOf();
  }

  private round2(value: number | null): number | null {
    return value !== null && value !== undefined
      ? Number(value.toFixed(2))
      : null;
  }

  // The first 5 series keep their designed palette stops; the 6th+ use a
  // deterministic golden-ratio spread (was Math.random(), which made those
  // lines change color on every re-render/zoom).
  private static readonly PALETTE_STOPS = [0, 1, 0.5, 0.25, 0.75];
  private static readonly GOLDEN_RATIO = 0.618033988749895;

  private getPaletteColor(index: number): string {
    const value =
      index < PortfolioChartComponent.PALETTE_STOPS.length
        ? PortfolioChartComponent.PALETTE_STOPS[index]
        : (index * PortfolioChartComponent.GOLDEN_RATIO) % 1;
    return d3
      .scaleLinear<string>()
      .domain([0, 0.25, 0.5, 0.75, 1])
      .range(this.colorPalette as any)(value);
  }
}
